import { forwardRef, ForwardRefRenderFunction, useImperativeHandle, useRef } from 'react';

import { WithStyles, withStyles } from '@core/theme/utils/with-styles';
import { AutocompleteRef } from '@shared/components/autocomplete';
import { DraggableList } from '@shared/components/draggable-list';
import { Flex } from '@shared/components/flex';
import {
  isUserValueSame,
  MixedUser,
  UserLookup,
  UserLookupCard,
  UserLookupProps,
} from '@shared/components/lookups/user-lookup';

import { styles } from './UserMultiLookup.styles';

export interface UserMultiLookupProps extends Omit<UserLookupProps, 'classes' | 'onSelect'>, WithStyles<typeof styles> {
  draggable?: boolean;
  showLookup?: boolean;
  showRole?: boolean;
  value?: MixedUser[];
  onChange?: (values: MixedUser[]) => void;
  onRemove?: (value: MixedUser) => void;
  onSelect?: UserLookupProps['onSelect'];
  onView?: (id: number) => void;
}

const UserMultiLookupComponent: ForwardRefRenderFunction<AutocompleteRef, UserMultiLookupProps> = (
  { classes, draggable, showLookup = true, showRole, value, onChange, onRemove, onSelect, onView, ...lookupProps },
  ref
) => {
  const lookupRef = useRef<AutocompleteRef>(null);
  useImperativeHandle(ref, () => lookupRef.current as AutocompleteRef);

  const renderCard = (item: MixedUser) => {
    return (
      <UserLookupCard
        classes={{ root: classes.lookupCard }}
        disabled={lookupProps.disabled}
        draggable={draggable}
        inputSize={lookupProps.inputProps?.size}
        item={item}
        key={'id' in item ? item.id : item.name}
        showRole={showRole}
        onRemove={
          onChange || onRemove
            ? (removedItem) => {
                if (onChange && value) {
                  onChange(value.filter((item) => !isUserValueSame(item, removedItem)));
                }
                if (onRemove) {
                  onRemove(removedItem);
                }
              }
            : undefined
        }
        onView={onView}
      />
    );
  };

  return (
    <Flex autoWidth={false} direction="column" classes={{ root: classes.root }}>
      {showLookup && (
        <UserLookup
          {...lookupProps}
          ref={lookupRef}
          onCreate={
            lookupProps.onCreate
              ? (data) => {
                  if (value?.some((item) => isUserValueSame(item, data))) {
                    return;
                  }
                  if (lookupProps.onCreate) {
                    lookupProps.onCreate(data);
                  }
                }
              : undefined
          }
          onSelect={(data) => {
            if (value?.some((item) => isUserValueSame(item, data))) {
              return;
            }
            if (onChange) {
              onChange(value ? [...value, data] : [data]);
            }
            if (onSelect) {
              onSelect(data);
            }
          }}
        />
      )}

      {!draggable && !!value?.length && (
        <Flex autoWidth={false} direction="column" classes={{ root: classes.list }}>
          {value.map((data) => renderCard(data))}
        </Flex>
      )}

      {!!draggable && !!value?.length && (
        <DraggableList
          classes={{ root: classes.draggableListRoot, list: classes.list, item: classes.draggableListItem }}
          disabled={lookupProps.disabled}
          items={value}
          onDragEnd={(items) => {
            if (onChange) {
              onChange(items as MixedUser[]);
            }
          }}
          renderItem={(item) => renderCard(item as MixedUser)}
        />
      )}
    </Flex>
  );
};

export const UserMultiLookup = withStyles(styles)(forwardRef(UserMultiLookupComponent));
